import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useCallback } from 'react';
import { tenantFetch } from '../lib/api-client';

interface ParsedRow {
  [key: string]: string;
}

interface ColumnMapping {
  csvColumn: string;
  targetField: string;
}

const TARGET_FIELDS = [
  { id: 'title', label: 'Title', required: true },
  { id: 'sku', label: 'SKU', required: false },
  { id: 'description', label: 'Description', required: false },
  { id: 'brand', label: 'Brand', required: false },
  { id: 'category', label: 'Category', required: false },
  { id: 'price', label: 'Price', required: false },
  { id: 'imageUrl', label: 'Image URL', required: false },
  { id: 'skip', label: '— Skip this column —', required: false },
];

export const Route = createFileRoute('/products_/import')({
  component: ImportProducts,
});

function ImportProducts() {
  const [step, setStep] = useState<'upload' | 'mapping' | 'preview' | 'importing' | 'complete'>('upload');
  const [, setFile] = useState<File | null>(null);
  const [, setCsvHeaders] = useState<string[]>([]);
  const [csvData, setCsvData] = useState<ParsedRow[]>([]);
  const [mappings, setMappings] = useState<ColumnMapping[]>([]);
  const [, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; errors: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  // Handle file drop
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && (droppedFile.type === 'text/csv' || droppedFile.name.endsWith('.csv'))) {
      handleFile(droppedFile);
    } else {
      setError('Please upload a CSV file');
    }
  }, []);

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFile(selectedFile);
    }
  };

  // Parse CSV file
  const handleFile = async (file: File) => {
    setFile(file);
    setError(null);
    
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        setError('CSV must have at least a header row and one data row');
        return;
      }
      
      // Parse headers
      const headers = parseCSVLine(lines[0]);
      setCsvHeaders(headers);
      
      // Parse data rows (limit preview to 100)
      const data: ParsedRow[] = [];
      for (let i = 1; i < Math.min(lines.length, 101); i++) {
        const values = parseCSVLine(lines[i]);
        const row: ParsedRow = {};
        headers.forEach((header, idx) => {
          row[header] = values[idx] || '';
        });
        data.push(row);
      }
      setCsvData(data);
      
      // Auto-map columns based on header names
      const autoMappings: ColumnMapping[] = headers.map(header => {
        const lowerHeader = header.toLowerCase().trim();
        let targetField = 'skip';
        
        if (lowerHeader.includes('title') || lowerHeader.includes('name') || lowerHeader === 'product') {
          targetField = 'title';
        } else if (lowerHeader.includes('sku') || lowerHeader.includes('code') || lowerHeader.includes('id')) {
          targetField = 'sku';
        } else if (lowerHeader.includes('desc')) {
          targetField = 'description';
        } else if (lowerHeader.includes('brand') || lowerHeader.includes('vendor') || lowerHeader.includes('manufacturer')) {
          targetField = 'brand';
        } else if (lowerHeader.includes('category') || lowerHeader.includes('type')) {
          targetField = 'category';
        } else if (lowerHeader.includes('price') || lowerHeader.includes('cost')) {
          targetField = 'price';
        } else if (lowerHeader.includes('image') || lowerHeader.includes('photo') || lowerHeader.includes('url')) {
          targetField = 'imageUrl';
        }
        
        return { csvColumn: header, targetField };
      });
      setMappings(autoMappings);
      
      setStep('mapping');
    } catch (err) {
      setError('Failed to parse CSV file');
    }
  };

  // Simple CSV line parser (handles quoted values)
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    
    return result;
  };

  // Update mapping
  const updateMapping = (csvColumn: string, targetField: string) => {
    setMappings(prev => prev.map(m => 
      m.csvColumn === csvColumn ? { ...m, targetField } : m
    ));
  };

  // Check if required fields are mapped
  const hasRequiredFields = () => {
    return mappings.some(m => m.targetField === 'title');
  };

  // Import products
  const handleImport = async () => {
    setImporting(true);
    setError(null);
    setStep('importing');
    
    try {
      // Transform data based on mappings
      const products = csvData.map(row => {
        const product: Record<string, string> = {};
        mappings.forEach(({ csvColumn, targetField }) => {
          if (targetField !== 'skip') {
            product[targetField] = row[csvColumn] || '';
          }
        });
        return product;
      }).filter(p => p.title); // Only include rows with a title
      
      const response = await tenantFetch('/api/products/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ products }),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Import failed');
      }
      
      const result = await response.json();
      setImportResult(result);
      setStep('complete');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
      setStep('preview');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/products" className="text-sm" style={{ color: '#A9A9A9' }}>
          ← Back to Products
        </Link>
      </div>
      
      <div>
        <h1 className="text-2xl font-light" style={{ color: '#171717' }}>Import Products</h1>
        <p className="text-base mt-1" style={{ color: '#A9A9A9' }}>
          Upload a CSV file to add products to your catalogue
        </p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center gap-4">
        {['Upload', 'Map Columns', 'Preview', 'Import'].map((label, idx) => {
          const stepIdx = ['upload', 'mapping', 'preview', 'importing'].indexOf(step);
          const isActive = idx <= stepIdx || step === 'complete';
          const isCurrent = idx === stepIdx;
          
          return (
            <div key={label} className="flex items-center gap-2">
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-mono"
                style={{ 
                  backgroundColor: isActive ? '#00FBA9' : '#FAFAFA',
                  color: isActive ? '#171717' : '#A9A9A9'
                }}
              >
                {step === 'complete' && idx === 3 ? '✓' : idx + 1}
              </div>
              <span 
                className="text-sm"
                style={{ color: isCurrent ? '#171717' : '#A9A9A9' }}
              >
                {label}
              </span>
              {idx < 3 && <span style={{ color: '#A9A9A9' }}>→</span>}
            </div>
          );
        })}
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 " style={{ backgroundColor: 'rgba(255,76,0,0.1)', color: '#FF4C00' }}>
          {error}
        </div>
      )}

      {/* Step: Upload */}
      {step === 'upload' && (
        <div 
          className={`card border-2 border-dashed p-12 text-center transition-colors cursor-pointer ${dragOver ? 'border-solid' : ''}`}
          style={{ borderColor: dragOver ? '#00FBA9' : 'rgba(169,169,169,0.3)' }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-input')?.click()}
        >
          <input 
            id="file-input"
            type="file" 
            accept=".csv"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FAFAFA' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#A9A9A9" strokeWidth="1.5">
              <path d="M12 15V3M12 3L8 7M12 3L16 7" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 17L2.621 19.485C2.72915 19.9177 2.97882 20.3018 3.33033 20.5763C3.68184 20.8508 4.11501 20.9999 4.561 21H19.439C19.885 20.9999 20.3182 20.8508 20.6697 20.5763C21.0212 20.3018 21.2708 19.9177 21.379 19.485L22 17" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          
          <p className="text-lg mb-2" style={{ color: '#171717' }}>
            {dragOver ? 'Drop your file here' : 'Drag and drop your CSV file'}
          </p>
          <p className="text-sm" style={{ color: '#A9A9A9' }}>
            or click to browse
          </p>
        </div>
      )}

      {/* Step: Mapping */}
      {step === 'mapping' && (
        <div className="card">
          <h2 className="text-lg font-medium mb-4" style={{ color: '#171717' }}>
            Map Your Columns
          </h2>
          <p className="text-sm mb-6" style={{ color: '#A9A9A9' }}>
            We've auto-detected some mappings. Adjust as needed.
          </p>
          
          <div className="space-y-3">
            {mappings.map(({ csvColumn, targetField }) => (
              <div key={csvColumn} className="flex items-center gap-4">
                <div className="w-1/3 px-4 py-2  text-sm font-mono truncate" style={{ backgroundColor: '#FAFAFA', color: '#171717' }}>
                  {csvColumn}
                </div>
                <span style={{ color: '#A9A9A9' }}>→</span>
                <select
                  value={targetField}
                  onChange={(e) => updateMapping(csvColumn, e.target.value)}
                  className="flex-1 px-4 py-2  border text-sm"
                  style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                >
                  {TARGET_FIELDS.map(field => (
                    <option key={field.id} value={field.id}>
                      {field.label} {field.required && '*'}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          
          {!hasRequiredFields() && (
            <p className="text-sm mt-4" style={{ color: '#FF4C00' }}>
              * Title is required. Please map a column to Title.
            </p>
          )}
          
          <div className="flex gap-3 mt-6">
            <button onClick={() => setStep('upload')} className="btn-secondary">
              Back
            </button>
            <button 
              onClick={() => setStep('preview')} 
              disabled={!hasRequiredFields()}
              className="btn-primary"
            >
              Preview Import
            </button>
          </div>
        </div>
      )}

      {/* Step: Preview */}
      {step === 'preview' && (
        <div className="card">
          <h2 className="text-lg font-medium mb-4" style={{ color: '#171717' }}>
            Preview ({csvData.length} products)
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ borderColor: 'rgba(169,169,169,0.1)' }}>
                  {mappings.filter(m => m.targetField !== 'skip').map(m => (
                    <th key={m.targetField} className="px-4 py-2 text-left font-medium" style={{ color: '#A9A9A9' }}>
                      {TARGET_FIELDS.find(f => f.id === m.targetField)?.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {csvData.slice(0, 10).map((row, idx) => (
                  <tr key={idx} className="border-b" style={{ borderColor: 'rgba(169,169,169,0.1)' }}>
                    {mappings.filter(m => m.targetField !== 'skip').map(m => (
                      <td key={m.targetField} className="px-4 py-2 truncate max-w-xs" style={{ color: '#171717' }}>
                        {row[m.csvColumn] || '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {csvData.length > 10 && (
            <p className="text-sm mt-4" style={{ color: '#A9A9A9' }}>
              Showing first 10 of {csvData.length} products
            </p>
          )}
          
          <div className="flex gap-3 mt-6">
            <button onClick={() => setStep('mapping')} className="btn-secondary">
              Back
            </button>
            <button onClick={handleImport} className="btn-primary">
              Import {csvData.length} Products
            </button>
          </div>
        </div>
      )}

      {/* Step: Importing */}
      {step === 'importing' && (
        <div className="card text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center animate-pulse" style={{ backgroundColor: 'rgba(0,251,169,0.2)' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#00FBA9" strokeWidth="2" className="animate-spin">
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
            </svg>
          </div>
          <p className="text-lg" style={{ color: '#171717' }}>Importing products...</p>
          <p className="text-sm mt-2" style={{ color: '#A9A9A9' }}>This may take a moment</p>
        </div>
      )}

      {/* Step: Complete */}
      {step === 'complete' && importResult && (
        <div className="card text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(0,251,169,0.2)' }}>
            <span className="text-2xl">✓</span>
          </div>
          <p className="text-lg" style={{ color: '#171717' }}>Import Complete!</p>
          <p className="text-sm mt-2" style={{ color: '#A9A9A9' }}>
            {importResult.imported} products imported successfully
            {importResult.errors > 0 && `, ${importResult.errors} errors`}
          </p>
          
          <div className="flex gap-3 justify-center mt-6">
            <Link to="/products" className="btn-primary">
              View Products
            </Link>
            <button onClick={() => { setStep('upload'); setFile(null); setCsvData([]); }} className="btn-secondary">
              Import More
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

